<!-- SurescriptsDocumentSearch.vue -->
<script lang="ts" setup>
import { reactive, onBeforeMount, watch } from 'vue';
import { ICol } from '../../lib/ChoiceTableHelpers';
import { ChoiceApi } from '../../LibMain';
import PunchList from '../../components/Messages/PunchList.vue';
import { PunchlistItemState, PunchListState, PunchlistStateType } from '../../components/Messages/PunchListModels';
import CreateCustomerSupportTicketLink from '../../components/Communication/CreateCustomerSupportTicketLink.vue';
import { IPatientSureScriptsInfo } from '../../lib/Models/Patient/IPatientSureScriptsInfo';
import { prettyDateTime, prettyTime } from '../../lib/DateUtils';
import CheckboxSwitch from '../../components/Inputs/CheckboxSwitch.vue';
import { confirmDialog, popAlert } from '../../lib/Notification';
import LoadingSection from '../../components/LoadingSection.vue';
import AlertSection from '../../components/Messages/AlertSection.vue';
import { DisplayStyle, IconTypes, TitleType } from '../../Enums/UiEnums';
import Icon from '../../components/Icon.vue';
import { orderTitles, SearchPuchlistOrder, surescriptsApiCallTime } from './SurescriptsDocumentSearchConstants';
import { IFindPatientResp, IGetDocumentListResp } from '../../lib/Api/SurescriptsRecordLocator';
import MessageBadge from '../../components/Messages/MessageBadge.vue';
import ChoiceButton from '../../components/Buttons/ChoiceButton.vue';
import { ActionButtonStyle } from '../../lib/Types/Types';
import Condition from '../../components/Condition.vue';
import Title from '../../components/Section/Title.vue';
import { useRouter } from 'vue-router';
import { initPinia } from '../../lib/PiniaMgr';
import { useSurescriptsRecordLocatorStore } from "../../lib/Stores/SurescriptsRecordLocatorStore"
import { assertInternally } from '../../lib/Environment';

    const surescriptsRecordLocatorStore = useSurescriptsRecordLocatorStore(initPinia());

    const props = defineProps<{
        patientId: number,
        isTest?: boolean        // Used to indicate we're performing a test
    }>();

    const compData = reactive<{
            surescriptsInfo?: IPatientSureScriptsInfo,
            updatingConsentState?: boolean,
            punchList?: PunchListState<SearchPuchlistOrder>,
            showRetryButton?: boolean,
            findPatientsResp?: IFindPatientResp,
            getDocumentListResp?: IGetDocumentListResp,
            errorInfo:
            {
                patientSearchErrorMsg?: string,
                getDocumentListErrorMsg?: string,
            }
        }>({errorInfo:{}});

    const router = useRouter();

    onBeforeMount(async () =>{
        if (!props.patientId || props.patientId < 0)
        {
            const errorMsg = `Patient ID not passed to SurescriptsDocumentSearch: ${props.patientId ?? "(undefined)"}`;
            compData.errorInfo.patientSearchErrorMsg = errorMsg;
            await popAlert(errorMsg);
            return;
        }

        await executePunchlist();
    });

    async function executePunchlist()
    {
        compData.punchList = new PunchListState<SearchPuchlistOrder>(orderTitles);

        if (!props.patientId)
            return;

        const surescriptsInfo = await ChoiceApi.patient.getPatientSureScriptsInfo(props.patientId);
        if (!surescriptsInfo)
        {
            compData.punchList?.updateState(SearchPuchlistOrder.RLEConsent, PunchlistItemState.Error);
            return;
        }
        compData.surescriptsInfo = surescriptsInfo;

        compData.punchList.updateState(SearchPuchlistOrder.RLEConsent, PunchlistItemState.Complete);

        loadPatientIntoRLE();        
    }

    watch(() => props.patientId, (newValue, oldValue) => { 
        if (newValue !== oldValue) { 
            surescriptsRecordLocatorStore.clearDocumentList();

            // If we have a valid patient ID, navigate back to the root page
            if (newValue)
            {
                executePunchlist();
            }
        }
    });

    function loadPatientIntoRLE()
    {
        compData.punchList?.updateState(SearchPuchlistOrder.LoadPatientIntoRLE, PunchlistItemState.Processing);

        ChoiceApi.surescriptsRLE.loadPatientIntoRLE(props.patientId).then(resp => {
            if (undefined == resp)
            {
                compData.punchList?.updateState(SearchPuchlistOrder.LoadPatientIntoRLE, PunchlistItemState.Error);
                return;
            }
            compData.punchList?.updateState(SearchPuchlistOrder.LoadPatientIntoRLE, PunchlistItemState.Complete);
            cachedDocumentCheck();
        });
    }

    function cachedDocumentCheck()
    {
        compData.punchList?.updateState(SearchPuchlistOrder.CachedDocumentCheck, PunchlistItemState.Processing);

        ChoiceApi.surescriptsRLE.getCachedDocuments(props.patientId).then(resp => {
            if (undefined == resp)
            {
                compData.punchList?.updateState(SearchPuchlistOrder.CachedDocumentCheck, PunchlistItemState.Error);
                return;
            }

            if (resp.documentListFetchedOnDateTimeUTC)
            {
                surescriptsRecordLocatorStore.updateDocuments(resp.documents, resp.documentListFetchedOnDateTimeUTC);
                router.push({ name: "Documents", params:{} }); 
                return;
            }

            surescriptsRecordLocatorStore.clearDocumentList();

            compData.punchList?.updateState(SearchPuchlistOrder.CachedDocumentCheck, PunchlistItemState.Complete);
            findPatient();
        });        
    }

    function findPatient()
    {
        compData.punchList?.updateState(SearchPuchlistOrder.SearchPatientMatch, PunchlistItemState.Processing);

        // If we're testing, modify the patient ID
        //const patientId = props.isTest ? -1 * props.patientId : props.patientId;
        const patientId = props.patientId;

        ChoiceApi.surescriptsRLE.findPatient(patientId).then(resp => {
            compData.findPatientsResp = resp;
            if (undefined == resp)
            {
                compData.punchList?.updateState(SearchPuchlistOrder.SearchPatientMatch, PunchlistItemState.Error);
                return;
            }

            if (resp.blockingStartTimestampUTC)
            {
                compData.errorInfo.patientSearchErrorMsg = "Patient Search currently in progress";
                if (compData.findPatientsResp?.blockingExpectedEndTimestampUTC)
                {
                    compData.errorInfo.patientSearchErrorMsg += ` It looks like the search should be complete by ${prettyTime(compData.findPatientsResp?.blockingExpectedEndTimestampUTC)}`;
                }
                else
                {
                    compData.errorInfo.patientSearchErrorMsg += ` It generally takes ${surescriptsApiCallTime.patientSearchInSeconds} seconds for a search to complete.`;
                }

                compData.punchList?.updateState(SearchPuchlistOrder.SearchPatientMatch, PunchlistItemState.Error);
                return;
            }

            compData.punchList?.updateState(SearchPuchlistOrder.SearchPatientMatch, PunchlistItemState.Complete);
            getDocumentsList(resp);
            return;
        });        
    }

    function getDocumentsList(patientInfo: IFindPatientResp)
    {
        if (!patientInfo.surescriptsPatientId)
        {
            assertInternally("patientInfo.surescriptsPatientId is undefined");
            compData.punchList?.updateState(SearchPuchlistOrder.GetDocumentList, PunchlistItemState.Error);
            return;
        }
        if (!patientInfo.assigningAuthorityId)
        {
            assertInternally("patientInfo.assigningAuthorityId is undefined");
            compData.punchList?.updateState(SearchPuchlistOrder.GetDocumentList, PunchlistItemState.Error);
            return;
        }
        if (!patientInfo.assigningAuthorityId)
        {
            compData.punchList?.updateState(SearchPuchlistOrder.GetDocumentList, PunchlistItemState.Error);
            return;
        }


        compData.punchList?.updateState(SearchPuchlistOrder.GetDocumentList, PunchlistItemState.Processing);

        compData.errorInfo.getDocumentListErrorMsg = undefined;

        ChoiceApi.surescriptsRLE.getDocumentList(props.patientId, patientInfo.surescriptsPatientId).then(resp => {
            if (!resp)
            {
                compData.punchList?.updateState(SearchPuchlistOrder.GetDocumentList, PunchlistItemState.Error);
                return;                
            }

            // Is someone already searching?
            if (resp.blockingStartTimestampUTC)
            {
                compData.errorInfo.getDocumentListErrorMsg = "Document Search currently in progress.";
                if (resp.blockingExpectedEndTimestampUTC)
                {
                    compData.errorInfo.getDocumentListErrorMsg += ` It looks like the search should be complete by ${prettyTime(resp.blockingExpectedEndTimestampUTC)}`;
                }
                else
                {
                    compData.errorInfo.getDocumentListErrorMsg += ` It generally takes ${surescriptsApiCallTime.getDocumentList} seconds for a search to complete.`;
                }

                compData.punchList?.updateState(SearchPuchlistOrder.GetDocumentList, PunchlistItemState.Error);
                return;
            }

            surescriptsRecordLocatorStore.updateDocuments(resp.documents, new Date());

            compData.punchList?.updateState(SearchPuchlistOrder.GetDocumentList, PunchlistItemState.Complete);
            router.push({ name: "Documents", params:{} }); 
        });
    }

    const documentSearchCols: ICol[] = [
        { Name: "Document"},
        { Name: "Date/Time"}
    ];

    async function updateSurescriptsConsent(consentGiven: boolean): Promise<boolean>
    {
        async function updatedConsent() {
            const confirmed = await confirmDialog("Change Surescripts RLE consent?");
            if (!confirmed)
                return false;

            const newSurescriptsInfo = await ChoiceApi.patient.updatePatientSureScriptsConsent(props.patientId, consentGiven);
            
            if (!newSurescriptsInfo)
                return false;
            compData.surescriptsInfo = newSurescriptsInfo;

            // If the patient just gave consent, perform a search!
            if (compData.surescriptsInfo.consentGiven)
            {
                await executePunchlist();
            }
            
            return true;            
        }

        compData.updatingConsentState = true;
        const result = await updatedConsent();
        compData.updatingConsentState = false;
        return result;
    }

    function onPunchlistStateChange(state?: PunchlistStateType)
    {
        if (state === PunchlistStateType.Error)
        {
            compData.showRetryButton = true;
        }
    }

    function retryDocumentSync()
    {
        compData.showRetryButton = false;
        compData.errorInfo.patientSearchErrorMsg = undefined;
        compData.errorInfo.getDocumentListErrorMsg = undefined;

        // Reset everything but RLE consent
        compData.punchList?.reset([SearchPuchlistOrder.RLEConsent]);
        loadPatientIntoRLE();        
    }
</script>

<style scoped>
    .status-container
    {
        display: flex;
        height: 100%;
        justify-content: center;
        align-items: center;
        text-align: center;
    }

    .phase
    {
        font-size: x-large;
        font-weight: bold;
    }

    .explination
    {
        color: grey
    }
</style>

<template>
    <Title :type="TitleType.PageTitle">
        Record Locator &amp; Exchange Consent
    </Title>

    <!-- Error messages -->
    <AlertSection v-if="compData.errorInfo.patientSearchErrorMsg" :displayStyle="DisplayStyle.Warning">
        {{ compData.errorInfo.patientSearchErrorMsg }}
    </AlertSection>
    <AlertSection v-if="compData.errorInfo.getDocumentListErrorMsg" :displayStyle="DisplayStyle.Warning">
        {{ compData.errorInfo.getDocumentListErrorMsg }}
    </AlertSection>
    
    <!-- Search Content -->
    <template v-if="compData.punchList">
        <div v-if="compData.surescriptsInfo">
            <div>
                <LoadingSection :isLoading="compData.updatingConsentState ?? false" :loadingMsg="'Upating Consent'">
                    <CheckboxSwitch v-model="compData.surescriptsInfo.consentGiven" :beforeChange="updateSurescriptsConsent">
                        <template #checked>On</template>
                        <template #unchecked>Off</template>
                    </CheckboxSwitch>
                </LoadingSection>
            </div>

            <Title :type="TitleType.SectionTitle">
                <b>
                    Last Modified By: {{ compData.surescriptsInfo.lastUpdatedUserId ?? "???"}}
                </b>
                on {{ prettyDateTime(compData.surescriptsInfo.lastUpdatedUTC) ?? "???" }}
            </Title>
        </div>
        
        <template v-if="compData.surescriptsInfo?.consentGiven">
            <AlertSection :displayStyle="DisplayStyle.Info" :dismissible="true" class="mt-2">
                <Icon :icon="IconTypes.CircleInfo"/>
                RecordSync can take up to 3 minutes. Feel free to work on other tasks in the meantime. You'll be notified when it completes.
            </AlertSection>    
    
            <PunchList :punchList="compData.punchList" @state="onPunchlistStateChange">
                <template #subTitle="{ item }">
                    <div v-if="PunchlistItemState.Error === item.state" class="text-muted ms-1">
        
                        <div v-if="SearchPuchlistOrder.LoadPatientIntoRLE == item.order">
                            Please try again. 
                            <div>
                                If this issue keeps happening, <CreateCustomerSupportTicketLink>contact Customer Support.</CreateCustomerSupportTicketLink>
                            </div>
                        </div>
    
                        <div v-if="SearchPuchlistOrder.SearchPatientMatch == item.order">
                            <Condition :condition="!!compData.errorInfo.patientSearchErrorMsg">
                                <template #true>
                                    <MessageBadge :displayStyle="DisplayStyle.Danger">
                                        {{ compData.errorInfo.patientSearchErrorMsg }}
                                    </MessageBadge>    
                                </template>
                                <template #false>
                                    No record found for this patient.  Please try your search again.
                                </template>
                            </Condition>
                            <div>
                                If this issue keeps happening, <CreateCustomerSupportTicketLink>contact Customer Support.</CreateCustomerSupportTicketLink>
                            </div>
                        </div>
    
                        <div v-if="SearchPuchlistOrder.GetDocumentList == item.order">
                            <Condition :condition="!!compData.errorInfo.getDocumentListErrorMsg">
                                <template #true>
                                    <MessageBadge :displayStyle="DisplayStyle.Danger">
                                        {{ compData.errorInfo.getDocumentListErrorMsg }}
                                    </MessageBadge>    
                                </template>
                                <template #false>
                                    Error trying to get documents for this patient.  Please try your search again.
                                </template>
                            </Condition>                    
                            <div>
                                If this issue keeps happening, <CreateCustomerSupportTicketLink>contact Customer Support.</CreateCustomerSupportTicketLink>
                            </div>
                        </div>
                    </div>
        
                    <div v-if="PunchlistItemState.Complete === item.state" class="text-muted ms-1">
                        <div v-if="SearchPuchlistOrder.CachedDocumentCheck == item.order">
                            <template v-if="!surescriptsRecordLocatorStore.cachedDocumentsDateUTC">
                                No cached documents available.
                            </template>
                        </div>
                    </div>
                </template>
            </PunchList>
        </template>

        <AlertSection v-if="!compData.surescriptsInfo?.consentGiven" :displayStyle="DisplayStyle.Warning">
            Consent has not been obtained from the patient.
        </AlertSection>

    </template>

    <div v-if="compData.showRetryButton">
        <ChoiceButton :action="ActionButtonStyle.Refresh" @click="retryDocumentSync">
            Retry
        </ChoiceButton>
    </div>
</template>